import React, { Suspense } from "react";

import type { ISession } from "@mne-authentication/api";
import { BrowserRouter } from "react-router";

import LinearProgress from "@mui/material/LinearProgress";

import { ArgusMenu, getMenuConfig, permissionCheckerUtil } from "@mnemonic/argus-menu";
import { getPackageBasename } from "@mnemonic/core-vite/utils";
import type { IntlShape } from "@mnemonic/intl";
import { RawIntlProvider } from "@mnemonic/intl";
import Logger from "@mnemonic/logging";
import type { TArgusLocale } from "@mnemonic/mui";
import { RemoveSplash } from "@mnemonic/mui";
import * as ArgusUtils from "@mnemonic/pure-utils/ArgusUtils";
import { Routes } from "@mnemonic/router";

import { ApplicationSession } from "./ApplicationWrappers/ApplicationSession";
import ThemeProvider from "./ApplicationWrappers/ThemeProvider";
import { toSessionAdapter } from "./Bootstrap/initializeSession";
import TopLevelErrorBoundary from "./Components/TopLevelErrorBoundary";
import { routeDefinitions } from "./Routes/routeDefinitions";

interface IApplicationProps {
  intl: IntlShape;
  session: ISession;
}

const Application = ({ session, intl }: IApplicationProps) => {
  const { domain, apiUrl } = ArgusUtils.getTargetMeta();
  const sessionAdapter = toSessionAdapter(session);
  const menuConfig = getMenuConfig(domain, sessionAdapter?.user, intl?.locale as TArgusLocale, Logger.getInstance());

  return (
    <TopLevelErrorBoundary>
      <ThemeProvider session={session} intl={intl}>
        <RawIntlProvider value={intl}>
          <ApplicationSession session={session} apiUrl={apiUrl}>
            <BrowserRouter basename={getPackageBasename(location.href, import.meta.env.DEV, import.meta.env.BASE_URL)}>
              <ArgusMenu
                {...{
                  ...permissionCheckerUtil(sessionAdapter, menuConfig),
                  logger: Logger.getInstance(),
                }}
              >
                <Suspense fallback={<LinearProgress color="secondary" />}>
                  <TopLevelErrorBoundary>
                    <RemoveSplash />
                    <Routes context={{ session }} routeDefinitions={routeDefinitions} />
                  </TopLevelErrorBoundary>
                </Suspense>
              </ArgusMenu>
            </BrowserRouter>
          </ApplicationSession>
        </RawIntlProvider>
      </ThemeProvider>
    </TopLevelErrorBoundary>
  );
};

export default Application;
